import { useTranslation } from "@redotech/redo-i18n";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  WalkthroughBackButton,
  WalkthroughNavButtons,
} from "./walkthrough-nav-buttons";
import * as overlayCss from "./walkthrough-overlay.module.css";
import { WalkthroughHighlightStep as HighlightStep } from "./walkthrough-types";

type HighlightStepType = Omit<HighlightStep, "title"> & { title: string };

const LOOK_HERE_DURATION = 800;

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type Placement = "top" | "bottom" | "left" | "right";

const PADDING = 8;
const TOOLTIP_GAP = 12;
const VIEWPORT_MARGIN = 12;
const PROGRESS_BAR_SELECTOR = '[data-testid="walkthrough-progress-bar"]';
const POLL_INTERVAL = 200;
const MAX_POLL_TIME = 3000;

function rectsEqual(a: TargetRect, b: TargetRect): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

function domRectToTargetRect(r: DOMRect): TargetRect {
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * Elements with `display: contents` have no box, so getBoundingClientRect()
 * returns {0,0,0,0}. Walk up to the nearest ancestor with a real layout box.
 */
function resolveVisualElement(el: Element): Element {
  let current = el;
  while (
    current.parentElement &&
    getComputedStyle(current).display === "contents"
  ) {
    current = current.parentElement;
  }
  return current;
}

/** Check available space and flip to the opposite side if needed. */
function resolveFlip(
  rect: TargetRect,
  preferred: Placement,
  tooltipWidth: number,
  tooltipHeight: number,
  viewportBottom: number,
): Placement {
  const spaceAbove = rect.top - PADDING - TOOLTIP_GAP;
  const spaceBelow =
    viewportBottom - (rect.top + rect.height + PADDING + TOOLTIP_GAP);
  const spaceLeft = rect.left - PADDING - TOOLTIP_GAP;
  const spaceRight =
    window.innerWidth - (rect.left + rect.width + PADDING + TOOLTIP_GAP);

  switch (preferred) {
    case "top":
      return spaceAbove >= tooltipHeight ? "top" : "bottom";
    case "bottom":
      return spaceBelow >= tooltipHeight ? "bottom" : "top";
    case "left":
      return spaceLeft >= tooltipWidth ? "left" : "right";
    case "right":
      return spaceRight >= tooltipWidth ? "right" : "left";
  }
}

export function computeTooltipPosition(
  rect: TargetRect,
  preferred: Placement,
  tooltipWidth: number,
  tooltipHeight: number,
  viewportBottom: number = window.innerHeight - VIEWPORT_MARGIN,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const placement = resolveFlip(
    rect,
    preferred,
    tooltipWidth,
    tooltipHeight,
    viewportBottom,
  );
  let top: number;
  let left: number;

  switch (placement) {
    case "top":
      top = rect.top - PADDING - TOOLTIP_GAP - tooltipHeight;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case "bottom":
      top = rect.top + rect.height + PADDING + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case "left":
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - PADDING - TOOLTIP_GAP - tooltipWidth;
      break;
    case "right":
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left + rect.width + PADDING + TOOLTIP_GAP;
      break;
  }

  // Clamp to viewport
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, vw - tooltipWidth - VIEWPORT_MARGIN),
  );
  top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(top, viewportBottom - tooltipHeight),
  );

  return { top, left };
}

export function shouldScrollHighlightTarget({
  hasExplicitScrollTarget,
  rect,
  viewportHeight,
}: {
  hasExplicitScrollTarget: boolean;
  rect: Pick<DOMRect, "bottom" | "top">;
  viewportHeight: number;
}): boolean {
  const headerOffset = 80;
  return (
    hasExplicitScrollTarget ||
    rect.top < headerOffset ||
    rect.bottom > viewportHeight
  );
}

function getTooltipViewportBottom(): number {
  const viewportBottom = window.innerHeight - VIEWPORT_MARGIN;
  const progressBar = document.querySelector(PROGRESS_BAR_SELECTOR);

  if (!progressBar) {
    return viewportBottom;
  }

  const progressRect = progressBar.getBoundingClientRect();
  return Math.min(viewportBottom, progressRect.top - VIEWPORT_MARGIN);
}

function getScrollElement(
  scrollTargetSelector: string | undefined,
  visualEl: Element,
): Element {
  if (!scrollTargetSelector) {
    return visualEl;
  }
  return document.querySelector(scrollTargetSelector) ?? visualEl;
}

export const WalkthroughHighlightStep = memo(function WalkthroughHighlightStep({
  step,
  stepIndex,
  totalSteps,
  next,
  back,
}: {
  step: HighlightStepType;
  stepIndex: number;
  totalSteps: number;
  next(): void;
  back(): void;
}) {
  const { t } = useTranslation();
  const [targetRect, setTargetRect] = useState<TargetRect | undefined>(
    undefined,
  );
  const lastRectRef = useRef<TargetRect | undefined>(undefined);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const lastFocusedStepRef = useRef("");

  // When the spotlight first appears (after a modal or dark backdrop), snap it
  // into position with no CSS transition. Once painted, re-enable transitions
  // so subsequent highlight-to-highlight moves animate smoothly.
  const [snap, setSnap] = useState(true);

  // Show the centered tooltip fallback only after a delay. This avoids a
  // flash on normal transitions (element found in ~16ms) while still giving
  // the user nav buttons if the element genuinely can't be found.
  const [showFallback, setShowFallback] = useState(false);

  // When the user clicks the "click the highlighted element" hint, run a
  // one-shot stronger pulse on the spotlight so attention is redirected
  // toward the actual click target instead of the hint chip.
  const [lookHereActive, setLookHereActive] = useState(false);

  // Discover the target element, then track its rect through three signals:
  //   - ResizeObserver on the element itself (size changes)
  //   - window scroll/resize (viewport changes)
  //   - MutationObserver on document.body (sibling reflows that move the
  //     element without resizing it, and remount cycles that replace the
  //     element with a new DOM node carrying the same testid)
  // Mutation-driven re-measures are coalesced through rAF so a burst of
  // DOM changes doesn't trigger a measurement storm.
  useEffect(() => {
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let elapsed = 0;
    let foundOnce = false;
    let cancelled = false;
    let trackedEl: Element | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let scheduledRaf: number | undefined;

    // Deduplication: only call setTargetRect when the rect actually changes.
    let lastRect: TargetRect | undefined = lastRectRef.current;

    function updateRect(r: DOMRect) {
      const newRect = domRectToTargetRect(r);
      if (lastRect && rectsEqual(lastRect, newRect)) {
        return;
      }
      lastRect = newRect;
      lastRectRef.current = newRect;
      setTargetRect(newRect);
    }

    function startTracking(el: Element) {
      // Drop the previous observer if we're retracking onto a fresh element.
      resizeObserver?.disconnect();
      trackedEl = el;
      resizeObserver = new ResizeObserver(() => {
        // ResizeObserver can fire on detached elements during remount.
        if (trackedEl?.isConnected) {
          updateRect(trackedEl.getBoundingClientRect());
        }
      });
      resizeObserver.observe(el);
    }

    function measure(): boolean {
      const el = document.querySelector(step.targetSelector);
      if (!el) {
        return false;
      }
      const visualEl = resolveVisualElement(el);

      // If the element was replaced (same selector, different DOM node),
      // retrack so ResizeObserver watches the live element.
      if (trackedEl !== visualEl) {
        startTracking(visualEl);
      }

      const r = visualEl.getBoundingClientRect();
      updateRect(r);

      if (!foundOnce) {
        foundOnce = true;
        const scrollEl = getScrollElement(step.scrollTargetSelector, visualEl);
        if (
          shouldScrollHighlightTarget({
            hasExplicitScrollTarget: step.scrollTargetSelector !== undefined,
            rect: r,
            viewportHeight: window.innerHeight,
          })
        ) {
          scrollEl.scrollIntoView({
            behavior: "smooth",
            block: step.scrollTargetBlock ?? "center",
          });
        }
      }
      return true;
    }

    function scheduleMeasure() {
      if (scheduledRaf !== undefined || cancelled) {
        return;
      }
      scheduledRaf = requestAnimationFrame(() => {
        scheduledRaf = undefined;
        if (!cancelled) {
          measure();
        }
      });
    }

    // If the new element already exists, measure() succeeds and CSS
    // transitions animate smoothly from the old position to the new one.
    // If it doesn't exist (e.g. lazy-loaded detail overlay), clear the
    // old rect so the tooltip doesn't flash at the wrong position. The
    // centered fallback tooltip will appear after 500ms (see effect
    // below) so the user has Back/Next controls and is never stuck.
    if (!measure()) {
      setTargetRect(undefined);
      lastRectRef.current = undefined;
      setSnap(true);

      pollTimer = setInterval(() => {
        elapsed += POLL_INTERVAL;
        if (measure()) {
          clearInterval(pollTimer);
        } else if (elapsed >= MAX_POLL_TIME) {
          // Stop polling but keep the fallback tooltip visible. We do
          // NOT auto-advance here — auto-advance cascades silently
          // through subsequent steps when their targets also need a
          // beat to appear, producing a confusing slideshow.
          clearInterval(pollTimer);
          if (!cancelled) {
            console.warn(
              `[Walkthrough] Target "${step.targetSelector}" not found after ${MAX_POLL_TIME}ms`,
            );
          }
        }
      }, POLL_INTERVAL);
    }

    function onViewportChange() {
      if (trackedEl?.isConnected) {
        updateRect(trackedEl.getBoundingClientRect());
        return;
      }
      // Tracked element is detached — try to recover via a fresh query.
      scheduleMeasure();
    }

    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearInterval(pollTimer);
      }
      if (scheduledRaf !== undefined) {
        cancelAnimationFrame(scheduledRaf);
      }
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [step.scrollTargetBlock, step.scrollTargetSelector, step.targetSelector]);

  // Measure tooltip size for positioning whenever the step or target changes
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const r = tooltipRef.current.getBoundingClientRect();
      setTooltipSize((prev) =>
        prev.width === r.width && prev.height === r.height
          ? prev
          : { width: r.width, height: r.height },
      );
    }
  }, [step.targetSelector, targetRect]);

  // Auto-focus tooltip for keyboard accessibility when step changes
  useEffect(() => {
    if (
      targetRect &&
      tooltipRef.current &&
      lastFocusedStepRef.current !== step.targetSelector
    ) {
      tooltipRef.current.focus();
      lastFocusedStepRef.current = step.targetSelector;
    }
  }, [targetRect, step.targetSelector]);

  // After the spotlight snaps into position, re-enable CSS transitions so
  // subsequent highlight-to-highlight moves animate smoothly.
  useEffect(() => {
    if (!(snap && targetRect)) {
      return;
    }
    const id = requestAnimationFrame(() => {
      setSnap(false);
    });
    return () => cancelAnimationFrame(id);
  }, [snap, targetRect]);

  // Start a timer when the target isn't found; show fallback after 500ms.
  // Reset immediately when the target is found.
  useEffect(() => {
    if (targetRect) {
      setShowFallback(false);
      return;
    }
    const timer = setTimeout(() => setShowFallback(true), 500);
    return () => clearTimeout(timer);
  }, [targetRect]);

  // Clear the look-here pulse after its animation finishes.
  useEffect(() => {
    if (!lookHereActive) {
      return;
    }
    const timer = setTimeout(
      () => setLookHereActive(false),
      LOOK_HERE_DURATION,
    );
    return () => clearTimeout(timer);
  }, [lookHereActive]);

  // Use the live rect if available, otherwise hold the last known position
  // so CSS transitions can animate the spotlight between nearby steps.
  // When the element doesn't exist yet (cleared in the effect), show a
  // full-screen dark backdrop instead of flashing at the wrong position.
  const displayRect = targetRect ?? lastRectRef.current;

  const placement = step.tooltipPlacement ?? "bottom";
  const isInteractive = !!step.interactive;
  const shouldAdvanceOnClick = step.interactive && step.advanceOn === "click";

  // When the target element hasn't been found yet, show the dark backdrop.
  // After 500ms, show a centered tooltip with nav buttons so the user isn't
  // stuck. The delay avoids a flash on normal transitions where the element
  // is found within a frame or two.
  if (!displayRect) {
    return (
      <>
        <div className={overlayCss.clickBlocker} />
        <div
          className={overlayCss.spotlight}
          style={{
            top: "50%",
            left: "50%",
            width: 0,
            height: 0,
            transition: "none",
          }}
        />
        {showFallback && (
          <div
            className={overlayCss.tooltip}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                next();
              }
            }}
            ref={tooltipRef}
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              transition: "none",
            }}
            tabIndex={-1}
          >
            <p className={overlayCss.tooltipTitle}>{step.title}</p>
            <p className={overlayCss.tooltipDescription}>{step.description}</p>
            <div className={overlayCss.tooltipFooter}>
              <span className={overlayCss.tooltipStepIndicator}>
                {t("{{step}} of {{totalSteps}}", {
                  step: stepIndex + 1,
                  totalSteps,
                })}
              </span>
              <div className={overlayCss.tooltipButtons}>
                <WalkthroughNavButtons
                  back={back}
                  next={next}
                  stepIndex={stepIndex}
                  totalSteps={totalSteps}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  const tooltipPos = computeTooltipPosition(
    displayRect,
    placement,
    tooltipSize.width,
    tooltipSize.height,
    getTooltipViewportBottom(),
  );

  const spotlightClassName = lookHereActive
    ? `${overlayCss.spotlight} ${overlayCss.lookHere}`
    : shouldAdvanceOnClick
      ? `${overlayCss.spotlight} ${overlayCss.pulse}`
      : overlayCss.spotlight;

  return (
    <>
      <div className={overlayCss.clickBlocker} />
      <div
        className={spotlightClassName}
        style={{
          top: displayRect.top - PADDING,
          left: displayRect.left - PADDING,
          width: displayRect.width + PADDING * 2,
          height: displayRect.height + PADDING * 2,
          pointerEvents: isInteractive ? "none" : undefined,
          transition: snap ? "none" : undefined,
        }}
      />
      {isInteractive && (
        <div
          className={overlayCss.interactivePassthrough}
          onClick={() => {
            if (!step.preventNavigation) {
              const el = document.querySelector(step.targetSelector);
              if (el instanceof HTMLElement) {
                // display:contents elements have no box; click a child cell instead
                const clickTarget =
                  getComputedStyle(el).display === "contents"
                    ? (el.querySelector('[role="cell"]') as HTMLElement | null)
                    : null;
                (clickTarget ?? el).click();
              }
            }
            if (shouldAdvanceOnClick) {
              requestAnimationFrame(() => next());
            }
          }}
          style={{
            top: displayRect.top - PADDING,
            left: displayRect.left - PADDING,
            width: displayRect.width + PADDING * 2,
            height: displayRect.height + PADDING * 2,
            transition: snap ? "none" : undefined,
          }}
        />
      )}
      <div
        className={overlayCss.tooltip}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isInteractive) {
            next();
          }
        }}
        ref={tooltipRef}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          transition: snap ? "none" : undefined,
        }}
        tabIndex={-1}
      >
        <p className={overlayCss.tooltipTitle}>{step.title}</p>
        {shouldAdvanceOnClick && (
          <button
            className={overlayCss.tooltipInteractiveHint}
            onClick={() => setLookHereActive(true)}
            type="button"
          >
            {t("Click the highlighted element to continue")}
          </button>
        )}
        <p className={overlayCss.tooltipDescription}>{step.description}</p>
        <div className={overlayCss.tooltipFooter}>
          <span className={overlayCss.tooltipStepIndicator}>
            {t("{{step}} of {{totalSteps}}", {
              step: stepIndex + 1,
              totalSteps,
            })}
          </span>
          <div className={overlayCss.tooltipButtons}>
            {shouldAdvanceOnClick ? (
              <WalkthroughBackButton back={back} stepIndex={stepIndex} />
            ) : (
              <WalkthroughNavButtons
                back={back}
                next={next}
                stepIndex={stepIndex}
                totalSteps={totalSteps}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
});
